// ════════════════════════════════════════════════════════════
//  worldChat.js — THE WORLD CHAT (one room for everyone watching)
// ════════════════════════════════════════════════════════════
//  caP'n, 2026-10-17: "I do want to be able to have a world chat while you're in there."
//  It rides the Empire's existing zone-chat node, so NO new rules and no new node:
//      zones/shatterproof-world/chat/{msgId} = { uid, name, text, ts }
//  (rules.json: read = signed in at all, write = your own uid only — the same rule
//  the Pier's chat lives under.) One world, not one per server: the League is one
//  shared canon world (caP'n 2026-10-16).
//
//  Guests may READ; only a really-signed-in account (`canSend`) may write.
//  Nothing here touches the fight: it is a DOM overlay with its own listener.
// ════════════════════════════════════════════════════════════
import { useCallback, useEffect, useRef, useState } from "react";
import { ref, push, onValue, remove, query, orderByChild, limitToLast } from "firebase/database";
import { db } from "../firebase";
import { serverNow } from "./ie.js";
import { SIGN } from "../data/skybox.js";

export const WORLD_ZONE = "shatterproof-world";
export const CHAT = {
  keep: 40,                 // ⟵ DIAL messages shown (and fetched)
  maxAge: 15 * 60 * 1000,   // ⟵ DIAL ms a message stays on screen — a late arrival still sees the last quarter hour
  max: SIGN.max,            // the SIGN's limit (data/skybox.js) — caP'n 2026-10-17: "make it the same limit you can write on the signs" (client side — the rules don't cap length)
  gap: 1200,                // ⟵ DIAL ms between your own messages
  sweep: 30 * 60 * 1000,    // ⟵ DIAL your own messages older than this are deleted when you open the chat
};

export function useWorldChat({ uid, name, canSend, enabled = true }) {
  const [messages, setMessages] = useState([]);
  const [slow, setSlow] = useState(false);
  const last = useRef(0);

  useEffect(() => {
    if (!enabled || !uid) return undefined;
    const q = query(ref(db, `zones/${WORLD_ZONE}/chat`), orderByChild("ts"), limitToLast(CHAT.keep));
    return onValue(q, (snap) => {
      const now = serverNow(), out = [];
      snap.forEach((c) => {
        const m = { id: c.key, ...c.val() };
        if (!m || typeof m.text !== "string") return;
        if (m.uid === uid && now - m.ts > CHAT.sweep) { remove(ref(db, `zones/${WORLD_ZONE}/chat/${m.id}`)).catch(() => {}); return; }   // tidy up after yourself
        if (now - m.ts > CHAT.maxAge) return;
        out.push(m);
      });
      setMessages(out);
    }, () => setMessages([]));
  }, [uid, enabled]);

  const send = useCallback((raw) => {
    const text = String(raw || "").replace(/\p{Cc}/gu, " ").trim().slice(0, CHAT.max);
    if (!text || !canSend || !uid) return false;
    const now = Date.now();
    if (now - last.current < CHAT.gap) { setSlow(true); setTimeout(() => setSlow(false), 1600); return false; }
    last.current = now;
    push(ref(db, `zones/${WORLD_ZONE}/chat`), { uid, name: String(name || "Unclaimed").slice(0, 24), text, ts: serverNow() }).catch(() => {});
    return true;
  }, [uid, name, canSend]);

  return { messages, send, slow };
}
